import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { negate } from 'lodash';
import { AuditRuleService } from '../../services/audit-rule.service';
import { TranslateService } from '../../translate/translate.service';
import { FormService } from '../../services/form.service';
import { CustomValidators } from '../../services/helpers/form-custom-validators';
import { AngularUtility } from '../../utility/angular.utility';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import {
  EvaluationStandards,
  EVALUATION_STANDARD_TO_AUDIT_STANDARD,
  EVALUATION_MEDIUM_TO_RULE_LIBRARY_CODE,
  EvaluationMediums,
  IDENTIFIER_TO_EVALUATION_STANDARDS,
} from '../../../../shared/constants/manual-evaluation';
import { IAuditStandard } from '../../../../shared/interfaces/audit-standard.interface';
import { $auditStandard, AuditDbStandard, AuditStandards } from '../../../../shared/constants/audit-standard';
import { wcagNameAndLevel } from '../../../../shared/constants/scanning';
import { SharedAuditStandardsUtility } from '../../../../shared/utils/audit-standards.utility';

enum FormFields {
  evaluationStandards = 'evaluationStandards',
  wcagStandard = 'wcagStandard',
}

@Component({
  selector: 'app-standards-selector',
  templateUrl: './standards-selector.component.html',
})
export class StandardsSelectorComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  public FormFields: typeof FormFields = FormFields;
  public evaluationStandards$: Observable<EvaluationStandards[]>;
  public wcagStandards$: Observable<EvaluationStandards[]>;
  public evaluationStandardsError$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public wcagStandardsError$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public EVALUATION_STANDARD_TO_AUDIT_STANDARD: typeof EVALUATION_STANDARD_TO_AUDIT_STANDARD =
    EVALUATION_STANDARD_TO_AUDIT_STANDARD;

  @Input() form: FormGroup;
  @Input() evaluationMedium: EvaluationMediums = EvaluationMediums.web;
  @Input() useRadioForWcag: boolean = true;
  @Output() standardsChange: EventEmitter<{
    evaluationStandards: EvaluationStandards[];
    conformanceLevel: wcagNameAndLevel | null;
  }> = new EventEmitter();

  private formFieldErrors: Record<
    string,
    { fieldLabel: string; fieldError$: BehaviorSubject<string>; overrideErrors?: Record<string, string> }
  >;

  constructor(
    private auditRuleService: AuditRuleService,
    private translateService: TranslateService,
    private formService: FormService,
  ) {}

  private initializeFormFieldErrors(): void {
    this.formFieldErrors = {
      [FormFields.evaluationStandards]: {
        fieldLabel: this.translateService.instant('accessibility_standard'),
        fieldError$: this.evaluationStandardsError$,
      },
      [FormFields.wcagStandard]: {
        fieldLabel: this.translateService.instant('wcag_standard'),
        fieldError$: this.wcagStandardsError$,
        overrideErrors: {
          required: this.translateService.instant('wcag_field_required_message'),
        },
      },
    };
  }

  private buildObservables(): void {
    const allStandards$: Observable<IAuditStandard[]> = this.auditRuleService
      .getStandardsForLibrary(EVALUATION_MEDIUM_TO_RULE_LIBRARY_CODE[this.evaluationMedium])
      .pipe(AngularUtility.shareRef());

    this.evaluationStandards$ = allStandards$.pipe(
      map((standards: IAuditStandard[]): EvaluationStandards[] => {
        const availableStandards: EvaluationStandards[] = standards
          .map((standard: IAuditStandard) => IDENTIFIER_TO_EVALUATION_STANDARDS[standard[$auditStandard.identifier]])
          .filter((standard: EvaluationStandards) => standard && !this.isWcag(standard))
          .sort();
        if (standards.some((standard: IAuditStandard) => standard[$auditStandard.standard] === AuditDbStandard.wcag)) {
          availableStandards.push(EvaluationStandards.WCAG_v20_A);
        }
        return availableStandards;
      }),
      AngularUtility.shareRef(),
    );

    this.wcagStandards$ = combineLatest([
      allStandards$,
      this.form
        .get(FormFields.evaluationStandards)
        .valueChanges.pipe(filter((standards: EvaluationStandards[]) => SharedCommonUtility.notNullish(standards))),
    ]).pipe(
      map(([allStandards, selectedStandards]: [IAuditStandard[], EvaluationStandards[]]): EvaluationStandards[] => {
        const isWcagSelected: boolean = selectedStandards.some(this.isWcag);
        if (!isWcagSelected) {
          return [];
        }
        return allStandards
          .filter((standard: IAuditStandard) => standard[$auditStandard.standard] === AuditDbStandard.wcag)
          .map((standard: IAuditStandard) => IDENTIFIER_TO_EVALUATION_STANDARDS[standard[$auditStandard.identifier]])
          .filter((standard: EvaluationStandards) => SharedCommonUtility.notNullishOrEmpty(standard))
          .sort();
      }),
      AngularUtility.shareRef(),
    );

    this.subscription.add(
      this.wcagStandards$.subscribe((wcagStandards: EvaluationStandards[]) => {
        if (wcagStandards.length > 0) {
          this.form.get(FormFields.wcagStandard).addValidators(CustomValidators.required);
        } else {
          this.form.get(FormFields.wcagStandard).removeValidators(CustomValidators.required);
          this.form.get(FormFields.wcagStandard).setValue(null);
        }
        this.form.get(FormFields.wcagStandard).updateValueAndValidity();
      }),
    );
  }

  private validateForm(): void {
    Object.keys(this.formFieldErrors).forEach((field: string) => {
      const { fieldLabel, fieldError$, overrideErrors } = this.formFieldErrors[field];
      fieldError$.next(this.formService.getErrorMessageForField(this.form.controls[field], fieldLabel, '', '', overrideErrors));
    });
  }

  public validate(): boolean {
    this.form.markAllAsTouched();
    this.validateForm();
    if (this.form.valid) {
      const selectedStandards: EvaluationStandards[] = this.getSelectedStandards();
      const conformanceLevel: wcagNameAndLevel = this.form.value.wcagStandard;
      if (!SharedCommonUtility.isNullishOrEmpty(selectedStandards)) {
        this.standardsChange.emit({ evaluationStandards: selectedStandards, conformanceLevel });
      }
    }
    return this.form.valid;
  }

  public isWcag(standard: EvaluationStandards): boolean {
    return EVALUATION_STANDARD_TO_AUDIT_STANDARD[standard] === AuditStandards.wcag;
  }

  protected getSelectedStandards(): EvaluationStandards[] {
    const nonWcagStandards: EvaluationStandards[] = this.form.value.evaluationStandards?.filter(negate(this.isWcag)) ?? [];
    const wcagStandards: EvaluationStandards[] = SharedCommonUtility.notNullish(this.form.value.wcagStandard)
      ? SharedAuditStandardsUtility.inflateWcagStandard(this.form.value.wcagStandard)
      : [];

    return [...nonWcagStandards, ...wcagStandards];
  }

  public standardClicked(checked: boolean, standard: EvaluationStandards): void {
    const evaluationStandardsField: AbstractControl<any, any> = this.form.get(FormFields.evaluationStandards);
    const oldValue: EvaluationStandards[] = evaluationStandardsField.value ?? [];
    let newValue: EvaluationStandards[] = [...oldValue];
    if (checked) {
      newValue.push(standard);
    } else {
      newValue = newValue.filter((s: EvaluationStandards) => s !== standard);
    }
    evaluationStandardsField.patchValue(newValue);
  }

  public getConformanceTranslationKey(standard: EvaluationStandards): string {
    if ([EvaluationStandards.en301549, EvaluationStandards.section508].includes(standard)) {
      return standard.toString();
    }
    return `${standard.toString()}_concise`;
  }

  public trackByStandard(_index: number, standard: EvaluationStandards): string {
    return standard;
  }

  ngOnInit(): void {
    this.initializeFormFieldErrors();
    this.buildObservables();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
